import { updateProject } from "@/components/common/project-provider/update-project";
import { validateMultiRegistrationReport } from "@/registration-tools/utils/multi-registration-report";
import {
  selectActiveBackgroundTasks,
  selectBackgroundTasks,
} from "@/store/background-tasks/background-tasks-selector";
import {
  addBackgroundTask,
  updateBackgroundTask,
} from "@/store/background-tasks/background-tasks-slice";
import { AppDispatch, AppThunk, RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  BackendFileExportTaskBase,
  BackgroundTask,
  BackgroundTaskTypes,
  C2CRegistrationTask,
  CreateProjectSnapshotTask,
  RegisterMultiCloudDataSetTask,
  SceneConversionTask,
  isCaptur3dFloorplanGenerationResult,
  validateC2CRegistrationResult,
} from "@/utils/background-tasks";
import {
  selectAncestor,
  selectIElement,
} from "@faro-lotv/app-component-toolbox";
import { compareDateTimes } from "@faro-lotv/foundation";
import {
  GUID,
  IElementBase,
  isIElementBimModelGroup,
  isIElementGenericDataSession,
  isIElementSection,
  isIElementSectionDataSession,
  isIElementSectionLayer,
  isIElementVideoRecording,
} from "@faro-lotv/ielement-types";
import {
  BackgroundTaskDetails,
  BackgroundTaskState,
  ProgressApiClient,
  ProgressApiSupportedTaskTypes,
  isBackgroundTaskActive,
  isBackgroundTaskInfoProgress,
  isBackgroundTaskInfoState,
  isProgressApiSupportedTaskType,
  useApiClientContext,
  validateDownloadResult,
  validatePointcloudPartialResult,
  validateSnapshotCreationResult,
} from "@faro-lotv/service-wires";
import { memo, useCallback, useEffect, useRef } from "react";

/**
 * Delay between requests to the Progress API if there are active tasks.
 *
 * Uses a shorter delay to keep the user updated on the progress of their tasks.
 */
const ACTIVE_POLLING_INTERVAL_MS = 3_000;

/**
 * Delay between requests to the Progress API if there are NO active tasks.
 *
 * Uses a longer delay to reduce the load on the API.
 */
const IDLE_POLLING_INTERVAL_MS = 10_000;

/**
 * Create a BackgroundTask to track a backend task only if it's needed
 *
 * Do not track already completed, failed tasks or tasks specific for another user
 *
 * @param task Task data received from the ProgressApi
 * @param currentUserId id of the current user if this is an authenticated session
 * @param dispatch Function to update the application store
 */
function createTaskIfNeeded(
  task: BackgroundTaskDetails,
  currentUserId: GUID | undefined,
  dispatch: AppDispatch,
): void {
  // Ignore un-supported tasks
  if (!task.taskType || !isProgressApiSupportedTaskType(task.taskType)) {
    return;
  }

  // Ignore tasks for an user that is not the current one
  if (shouldHideForThisUser(task, currentUserId)) {
    return;
  }

  if (
    isBackgroundTaskInfoState(task.status) &&
    isBackgroundTaskActive(task.status.state)
  ) {
    // Init a new BackgroundTask object as the backend process is new but is active
    dispatch(
      addBackgroundTask({
        id: task.id,
        createdAt: task.createdAt,
        changedAt: task.status.changedAt,
        iElementId: task.context.elementId ?? task.context.correlationId,
        type: task.taskType,
        devMessage: task.status.devMessage,
        errorCode: task.status.errorCode ?? undefined,
        state: task.status.state,
        progress: 0,
        metadata: { jobId: task.context.jobId },
        tags: task.tags,
      }),
    );
  } else if (isBackgroundTaskInfoProgress(task.status)) {
    // Init a new BackgroundTask object as the backend process is already running
    dispatch(
      addBackgroundTask({
        id: task.id,
        createdAt: task.createdAt,
        changedAt: task.status.changedAt,
        iElementId: task.context.elementId ?? task.context.correlationId,
        type: task.taskType,
        devMessage: task.status.devMessage,
        errorCode: task.status.errorCode ?? undefined,
        state: BackgroundTaskState.started,
        progress:
          (task.status.progress.current / task.status.progress.total) * 100,
        metadata: { jobId: task.context.jobId },
        tags: task.tags,
      }),
    );
  }
  // Handle all the export tasks that are already completed
  else if (
    isExportTask(task.taskType) &&
    (task.status.state === BackgroundTaskState.succeeded ||
      task.status.state === BackgroundTaskState.failed)
  ) {
    const taskResult = validateDownloadResult(task.status.result)
      ? task.status.result
      : undefined;

    const { downloadUrl, fileName, ...result } = taskResult ?? {};

    dispatch(
      addBackgroundTask({
        id: task.id,
        createdAt: task.createdAt,
        changedAt: task.status.changedAt,
        iElementId: task.context.elementId ?? task.context.correlationId,
        devMessage: task.status.devMessage,
        errorCode: task.status.errorCode ?? undefined,
        state: task.status.state,
        progress: 100,
        tags: task.tags,
        type: task.taskType,
        metadata: {
          jobId: task.context.jobId,
          downloadUrl,
          fileName,
          result,
        },
      }),
    );
  } else if (
    task.taskType === ProgressApiSupportedTaskTypes.snapshotGeneration &&
    (task.status.state === BackgroundTaskState.succeeded ||
      task.status.state === BackgroundTaskState.failed)
  ) {
    const taskResult = validateSnapshotCreationResult(task.status.result)
      ? task.status.result
      : undefined;
    const { SnapshotProjectId, SnapshotName } = taskResult ?? {};
    dispatch(
      addBackgroundTask({
        id: task.id,
        createdAt: task.createdAt,
        changedAt: task.status.changedAt,
        iElementId: task.context.elementId ?? task.context.correlationId,
        devMessage: task.status.devMessage,
        errorCode: task.status.errorCode ?? undefined,
        state: task.status.state,
        progress: 100,
        tags: task.tags,
        type: task.taskType,
        metadata: {
          jobId: task.context.jobId,
          snapshotId: SnapshotProjectId,
          snapshotName: SnapshotName,
        },
      }),
    );
  } else if (task.status.state === BackgroundTaskState.succeeded) {
    // Track completed registration tasks to display metric reports
    // Maybe we can track this in a different way in the future https://faro01.atlassian.net/browse/NRT-688
    const { result } = task.status;

    const payload = {
      id: task.id,
      createdAt: task.createdAt,
      changedAt: task.status.changedAt,
      iElementId: task.context.elementId ?? task.context.correlationId,
      devMessage: task.status.devMessage,
      errorCode: task.status.errorCode ?? undefined,
      state: BackgroundTaskState.succeeded,
      progress: 100,
      metadata: { jobId: task.context.jobId },
      tags: task.tags,
    };

    switch (task.taskType) {
      case ProgressApiSupportedTaskTypes.registerMultiCloudDataSet: {
        dispatch(
          addBackgroundTask({
            ...payload,
            type: task.taskType,
            result: validateMultiRegistrationReport(result)
              ? result
              : undefined,
          }),
        );
        break;
      }
      case ProgressApiSupportedTaskTypes.captur3dFloorplanGeneration: {
        dispatch(
          addBackgroundTask({
            ...payload,
            type: task.taskType,
            result: isCaptur3dFloorplanGenerationResult(result)
              ? result
              : undefined,
          }),
        );
        break;
      }
    }
  } else if (
    task.status.state === BackgroundTaskState.failed &&
    task.taskType === ProgressApiSupportedTaskTypes.videoMode
  ) {
    dispatch(
      addBackgroundTask({
        id: task.id,
        createdAt: task.createdAt,
        changedAt: task.status.changedAt,
        iElementId: task.context.elementId ?? task.context.correlationId,
        devMessage: task.status.devMessage,
        errorCode: task.status.errorCode ?? undefined,
        state: task.status.state,
        progress: 100,
        tags: task.tags,
        type: task.taskType,
        metadata: {
          jobId: task.context.jobId,
        },
      }),
    );
  }
}

function computeTaskWithMetadata(
  task: BackgroundTaskDetails,
  backgroundTask: BackgroundTask,
): BackgroundTask {
  switch (backgroundTask.type) {
    case ProgressApiSupportedTaskTypes.structuredExport:
    case ProgressApiSupportedTaskTypes.pointCloudExport:
    case ProgressApiSupportedTaskTypes.orthophotoGeneration:
    case ProgressApiSupportedTaskTypes.projectReportGeneration: {
      const taskResult = validateDownloadResult(task.status.result)
        ? task.status.result
        : undefined;

      const { downloadUrl, fileName, ...result } = taskResult ?? {};
      return {
        ...backgroundTask,
        shouldPreventWindowClose: false,
        metadata: {
          ...backgroundTask.metadata,
          downloadUrl,
          fileName,
          result,
        },
      } satisfies BackendFileExportTaskBase;
    }
    case ProgressApiSupportedTaskTypes.snapshotGeneration: {
      const result = validateSnapshotCreationResult(task.status.result)
        ? task.status.result
        : undefined;
      const { SnapshotProjectId, SnapshotName } = result ?? {};
      return {
        ...backgroundTask,
        metadata: {
          ...backgroundTask.metadata,
          snapshotId: SnapshotProjectId,
          snapshotName: SnapshotName,
        },
      } satisfies CreateProjectSnapshotTask;
    }
    case ProgressApiSupportedTaskTypes.sceneConversion: {
      const result = validatePointcloudPartialResult(task.status.result)
        ? task.status.result
        : undefined;

      return {
        ...backgroundTask,
        metadata: {
          ...backgroundTask.metadata,
          result,
        },
      } satisfies SceneConversionTask;
    }
    case ProgressApiSupportedTaskTypes.c2cRegistration: {
      const result = validateC2CRegistrationResult(task.status.result)
        ? task.status.result
        : undefined;
      return {
        ...backgroundTask,
        result,
      } satisfies C2CRegistrationTask;
    }
    case ProgressApiSupportedTaskTypes.registerMultiCloudDataSet: {
      const result = validateMultiRegistrationReport(task.status.result)
        ? task.status.result
        : undefined;
      return {
        ...backgroundTask,
        result,
      } satisfies RegisterMultiCloudDataSetTask;
    }
  }
  return { ...backgroundTask };
}

/**
 * Update a background task with the new information received from the backend
 *
 * @param task information received from the ProgressApi
 * @param backgroundTask application stored information about this task
 * @param dispatch function to update the application state
 * @param onTaskCompleted callback called when a task move to the succeeded state
 */
function updateTask(
  task: BackgroundTaskDetails,
  backgroundTask: BackgroundTask,
  dispatch: AppDispatch,
  onTaskCompleted: (task: BackgroundTask) => void,
): void {
  // We received an update for a task that's in the store
  const taskWithMetadata = computeTaskWithMetadata(task, backgroundTask);
  if (
    isBackgroundTaskInfoState(task.status) &&
    backgroundTask.state !== task.status.state
  ) {
    // The task changed state
    const updatedTask: BackgroundTask = {
      ...taskWithMetadata,
      state: task.status.state,
      changedAt: task.status.changedAt,
      devMessage: task.status.devMessage,
      errorCode: task.status.errorCode ?? undefined,
      tags: task.tags,
    };
    dispatch(updateBackgroundTask(updatedTask));
    if (updatedTask.state === BackgroundTaskState.succeeded) {
      onTaskCompleted(updatedTask);
    }
  } else if (isBackgroundTaskInfoProgress(task.status)) {
    // The progress of this task changed
    const updatedTask: BackgroundTask = {
      ...taskWithMetadata,
      state: BackgroundTaskState.started,
      changedAt: task.status.changedAt,
      progress:
        (task.status.progress.current / task.status.progress.total) * 100,
    };
    dispatch(updateBackgroundTask(updatedTask));
  }
}

/**
 * A map from a background task type to a type guard.
 *
 * The type guard determines which ancestor is selected
 * for refreshing the IElement tree.
 */
const TASK_TO_ANCESTOR_TYPE_GUARD_MAP: Record<
  BackgroundTaskTypes,
  ((iElement: IElementBase) => boolean) | undefined
> = {
  FileUpload: isIElementSection,
  ManualBlurring: undefined,
  MultiFileUpload: isIElementSection,
  Mutation: undefined,
  Orthophoto: undefined,
  [ProgressApiSupportedTaskTypes.structuredExport]:
    isIElementGenericDataSession,
  [ProgressApiSupportedTaskTypes.pointCloudExport]:
    isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.pointCloudToPotree]:
    isIElementSectionDataSession,
  // TODO: Remove after backend update: https://faro01.atlassian.net/browse/SWEB-1980
  [ProgressApiSupportedTaskTypes.pointCloudLazToPotree]:
    isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.pointCloudE57ToLaz]:
    isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.pointCloudGSToLaz]:
    isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.c2cRegistration]: isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.registerMultiCloudDataSet]:
    isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.mergePointClouds]:
    isIElementSectionDataSession,
  [ProgressApiSupportedTaskTypes.bimModelImport]: isIElementBimModelGroup,
  [ProgressApiSupportedTaskTypes.videoMode]: isIElementVideoRecording,
  [ProgressApiSupportedTaskTypes.sceneConversion]: undefined,
  [ProgressApiSupportedTaskTypes.captur3dFloorplanGeneration]:
    isIElementSectionLayer,
  TiledImageSheetGeneration: undefined,
  [ProgressApiSupportedTaskTypes.assetDetection]: undefined,
  [ProgressApiSupportedTaskTypes.orthophotoGeneration]:
    isIElementGenericDataSession,
  [ProgressApiSupportedTaskTypes.projectReportGeneration]: undefined,
  [ProgressApiSupportedTaskTypes.snapshotGeneration]: undefined,
  [ProgressApiSupportedTaskTypes.vlmIndexMigration]: undefined,
};

/**
 * @returns a component to track the ProgressApi and keep the store in sync
 * Must be used within an `ApiClientContextProvider`.
 */
export const ProgressApiTracker = memo(function ProgressApiTracker(): null {
  const { progressApiClient: progressApi } = useApiClientContext();
  const { getState } = useAppStore();
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(selectCurrentUser)?.id;

  const { projectApiClient: projectApi } = useApiClientContext();

  const onTaskCompleted = useCallback(
    (task: BackgroundTask) => {
      // If the task is a SceneConversion, instead of fetching a subtree,
      // refresh the entire page so that the app can be correctly initialized
      if (task.type === ProgressApiSupportedTaskTypes.sceneConversion) {
        window.location.reload();
        return;
      }

      // The element of which the subtree needs to be fetched once the tasked is completed
      const ancestorToFetch = TASK_TO_ANCESTOR_TYPE_GUARD_MAP[task.type];

      if (ancestorToFetch) {
        if (!task.iElementId) return;

        const element = selectIElement(task.iElementId)(getState());
        // A background task updated an element that is not currently loaded, nothing to do
        if (!element) return;

        // A background task changed an element that is loaded, we need to refresh that element sub-tree
        // find the section containing this IElement to update
        const elementSection = selectAncestor(
          element,
          ancestorToFetch,
        )(getState());

        dispatch(
          updateProject({
            projectApi,
            iElementQuery: {
              ancestorIds: [elementSection?.id ?? element.id],
            },
          }),
        );
      }
    },
    [getState, dispatch, projectApi],
  );

  const updateTaskId = useRef<number | null>(null);

  // Update the tasks by continuously polling the Progress API
  useEffect(() => {
    const abortController = new AbortController();

    async function updateBackgroundTasks(): Promise<void> {
      await updateTasks(
        progressApi,
        currentUserId,
        dispatch,
        onTaskCompleted,
        abortController.signal,
      );

      const updateDelay = updateDelayMs(getState());

      updateTaskId.current = window.setTimeout(
        () => updateBackgroundTasks(),
        updateDelay,
      );
    }

    async function initiateUpdateLoop(): Promise<void> {
      try {
        // The general updates will loop
        // updateTasks called by updateBackgroundTasks catches any error and just acts like there
        // are no updates.
        await updateBackgroundTasks();

        // Scene is handled separately, but just once at the start
        // This avoids too sending too many requests to the ProgressAPI at once
        const bgTasks = selectBackgroundTasks()(getState());

        // Manage the scene conversions separately, since updateTasks requests only the first 30
        // jobs and the Scene conversion may not be there.
        // TODO: Improve the queries to the ProgressAPI https://faro01.atlassian.net/browse/SWEB-5358
        await updateSceneConversionTasks(
          progressApi,
          bgTasks,
          dispatch,
          abortController.signal,
        );
      } catch (error: unknown) {
        if (error !== "Cancel ProgressApiTracker") {
          console.error(error);
        }
      }
    }

    initiateUpdateLoop();

    return () => {
      // Stop the update loop
      if (updateTaskId.current !== null) {
        window.clearTimeout(updateTaskId.current);
      }

      // Cancel the ongoing request
      // Important, otherwise a new timeout might be started after the request completes
      // This could result in multiple concurrent update loops
      abortController.abort("Cancel ProgressApiTracker");
    };
  }, [currentUserId, dispatch, getState, onTaskCompleted, progressApi]);

  // Fetches all floor plan generation tasks, since the cloud activity menu is the only way to interact with them.
  useEffect(() => {
    const controller = new AbortController();

    async function fetchAllFloorPlanGenerationTasks(): Promise<void> {
      const tasks = await progressApi
        .requestAllProgress({
          signal: controller.signal,
          direction: "lastToFirst",
          taskType: ProgressApiSupportedTaskTypes.captur3dFloorplanGeneration,
        })
        // If the connection with the ProgressAPI fails, silently ignore it
        .catch(() => []);

      dispatch(createOrUpdateTasks(tasks, currentUserId, onTaskCompleted));
    }

    fetchAllFloorPlanGenerationTasks();

    return () => controller.abort();
  }, [currentUserId, dispatch, onTaskCompleted, progressApi]);

  return null;
});

/**
 * @param taskType the type of the task to check if it's an export task
 * @returns true if the task is an export task
 */
function isExportTask(taskType: string | null): boolean {
  return (
    taskType === ProgressApiSupportedTaskTypes.structuredExport ||
    taskType === ProgressApiSupportedTaskTypes.pointCloudExport ||
    taskType === ProgressApiSupportedTaskTypes.orthophotoGeneration ||
    taskType === ProgressApiSupportedTaskTypes.projectReportGeneration
  );
}

/**
 * @param task to check
 * @param userId of the current logged in user
 * @returns true if this task should be hidden from the current user
 */
function shouldHideForThisUser(
  task: BackgroundTaskDetails,
  userId?: GUID,
): boolean {
  return isExportTask(task.taskType) && task.context.userId !== userId;
}

/**
 * Create or update local tasks by requesting the current state to the ProgressAPI
 *
 * @param progressApi The client to make requests to the ProgressAPI
 * @param backgroundTasks The list of tasks currently stored by the app
 * @param currentUserId The id of the current user
 * @param dispatch The function to dispatch the updates to the store
 * @param onTaskCompleted A callback executed when a task is completed
 * @param signal An optional AbortSignal to cancel the request
 */
async function updateTasks(
  progressApi: ProgressApiClient,
  currentUserId: string | undefined,
  dispatch: AppDispatch,
  onTaskCompleted: (task: BackgroundTask) => void,
  signal?: AbortSignal,
): Promise<void> {
  // If the connection with the ProgressAPI fails do not error out but just
  // act like we got no updates as we don't want the app to fail if the ProgressAPI
  // does not respond
  const { data } = await progressApi
    .requestProgress({ signal })
    .catch(() => ({ data: [] }));

  dispatch(createOrUpdateTasks(data, currentUserId, onTaskCompleted));
}

/**
 * @returns redux action to create or update a list of newly fetched tasks, depending on whether they already exist in the store.
 * @param tasks the newly fetched tasks
 * @param currentUserId the current user id
 * @param onTaskCompleted callback, when a task was completed by an update
 */
function createOrUpdateTasks(
  tasks: BackgroundTaskDetails[],
  currentUserId: string | undefined,
  onTaskCompleted: (task: BackgroundTask) => void,
): AppThunk<void> {
  return (dispatch, getState) => {
    const allTasks = selectBackgroundTasks()(getState());

    for (const task of tasks) {
      const backgroundTask = allTasks.find(
        (backgroundTask) => backgroundTask.id === task.id,
      );
      if (backgroundTask) {
        updateTask(task, backgroundTask, dispatch, onTaskCompleted);
      } else {
        createTaskIfNeeded(task, currentUserId, dispatch);
      }
    }
  };
}

/**
 * Update the store with the state of the latest scene conversion task
 *
 * @param progressApi The client to make requests to the ProgressAPI
 * @param backgroundTasks The list of tasks currently stored by the app
 * @param dispatch The function to dispatch the updates to the store
 * @param signal An optional AbortSignal to cancel the request
 */
async function updateSceneConversionTasks(
  progressApi: ProgressApiClient,
  backgroundTasks: BackgroundTask[],
  dispatch: AppDispatch,
  signal?: AbortSignal,
): Promise<void> {
  const { data: sceneConversions } = await progressApi.requestProgress({
    taskType: ProgressApiSupportedTaskTypes.sceneConversion,
    signal,
  });
  // Sort the scene conversions by getting the latest first
  sceneConversions.sort((a, b) => compareDateTimes(b.createdAt, a.createdAt));
  const sceneConversion = sceneConversions.at(0);
  // If the latest task failed and the task is not in the store yet, add it
  if (
    sceneConversion?.status.state === BackgroundTaskState.failed &&
    !backgroundTasks.find(
      (backgroundTask) => backgroundTask.id === sceneConversion.id,
    )
  ) {
    dispatch(
      addBackgroundTask({
        id: sceneConversion.id,
        createdAt: sceneConversion.createdAt,
        changedAt: sceneConversion.status.changedAt,
        iElementId:
          sceneConversion.context.elementId ??
          sceneConversion.context.correlationId,
        type: ProgressApiSupportedTaskTypes.sceneConversion,
        devMessage: sceneConversion.status.devMessage,
        errorCode: sceneConversion.status.errorCode ?? undefined,
        state: sceneConversion.status.state,
        progress: 0,
        metadata: {
          jobId: sceneConversion.context.jobId,
          result: validatePointcloudPartialResult(sceneConversion.status.result)
            ? sceneConversion.status.result
            : undefined,
        },
        tags: sceneConversion.tags,
      }),
    );
  }
}

/**
 * Depending on the current state of background tasks, determine how long to wait for the next update.
 *
 * @param state The current application state
 * @returns The delay in milliseconds to use for the next update from the Progress API.
 */
function updateDelayMs(state: RootState): number {
  const areTasksActive = selectActiveBackgroundTasks(state).length > 0;

  return areTasksActive ? ACTIVE_POLLING_INTERVAL_MS : IDLE_POLLING_INTERVAL_MS;
}
